import api from './api';
import type {
  AdminUser,
  CreateUserFormData,
  UpdateUserFormData,
  LlmConfigsResponse,
  ProjectLlmInfo,
  AgentInfo,
  ModelTierInfo,
  ToolInfo,
  ServiceConfig,
  LlmProviderConfig,
  AgentConfigInfo,
  ModelParametersApi,
  ParameterConstraint,
  BuiltInToolSettings,
  BuiltInToolConfig,
} from '@/types';

// Backend API response types
interface BackendUserResponse {
  id: string;
  type: string;
  attributes: {
    id: string;
    email: string;
    name: string;
    avatar_url?: string;
    role: 'admin' | 'user';
    status: 'active' | 'inactive' | 'suspended';
    last_sign_in_at?: string;
    project_count?: number;
    created_at: string;
    updated_at: string;
  };
}

interface UsersListResponse {
  success: boolean;
  data: {
    data: BackendUserResponse[];
  };
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

interface UserResponse {
  success: boolean;
  data: BackendUserResponse;
}

// Transform backend user to frontend format
function transformUser(backendUser: BackendUserResponse): AdminUser {
  return {
    id: backendUser.id,
    email: backendUser.attributes.email,
    name: backendUser.attributes.name,
    avatarUrl: backendUser.attributes.avatar_url,
    role: backendUser.attributes.role,
    status: backendUser.attributes.status,
    lastLoginAt: backendUser.attributes.last_sign_in_at,
    projectCount: backendUser.attributes.project_count,
    createdAt: backendUser.attributes.created_at,
    updatedAt: backendUser.attributes.updated_at,
  };
}

export const adminService = {
  // GET /api/v1/admin/users
  async getUsers(page = 1, limit = 20): Promise<{ users: AdminUser[]; meta: { currentPage: number; totalPages: number; totalCount: number } }> {
    const response = await api.get<UsersListResponse>(`/admin/users?page=${page}&limit=${limit}`);
    return {
      users: response.data.data.data.map(transformUser),
      meta: {
        currentPage: response.data.meta.current_page,
        totalPages: response.data.meta.total_pages,
        totalCount: response.data.meta.total_count,
      },
    };
  },

  // GET /api/v1/admin/users/:id
  async getUser(userId: string): Promise<AdminUser> {
    const response = await api.get<UserResponse>(`/admin/users/${userId}`);
    return transformUser(response.data.data);
  },

  // POST /api/v1/admin/users
  async createUser(data: CreateUserFormData): Promise<AdminUser> {
    const response = await api.post<UserResponse>('/admin/users', {
      user: {
        name: data.name,
        email: data.email,
        password: data.password,
        role: data.role,
      },
    });
    return transformUser(response.data.data);
  },

  // PATCH /api/v1/admin/users/:id
  async updateUser(userId: string, data: UpdateUserFormData): Promise<AdminUser> {
    const payload: Record<string, unknown> = {};
    if (data.name) payload.name = data.name;
    if (data.email) payload.email = data.email;
    if (data.password) payload.password = data.password;
    if (data.role) payload.role = data.role;

    const response = await api.patch<UserResponse>(`/admin/users/${userId}`, {
      user: payload,
    });
    return transformUser(response.data.data);
  },

  // DELETE /api/v1/admin/users/:id
  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  // POST /api/v1/admin/users/:id/activate
  async activateUser(userId: string): Promise<AdminUser> {
    const response = await api.post<UserResponse>(`/admin/users/${userId}/activate`);
    return transformUser(response.data.data);
  },

  // POST /api/v1/admin/users/:id/deactivate
  async deactivateUser(userId: string): Promise<AdminUser> {
    const response = await api.post<UserResponse>(`/admin/users/${userId}/deactivate`);
    return transformUser(response.data.data);
  },

  // POST /api/v1/admin/users/:id/reset_password
  async resetPassword(userId: string): Promise<{ tempPassword: string }> {
    const response = await api.post<{ success: boolean; data: { temporary_password: string } }>(
      `/admin/users/${userId}/reset_password`
    );
    return { tempPassword: response.data.data.temporary_password };
  },

  // GET /api/v1/admin/llm_configs
  async getLlmConfigs(): Promise<LlmConfigsResponse> {
    interface BackendLlmConfigsResponse {
      success: boolean;
      data: {
        providers?: Record<string, {
          available: boolean;
          api_key_configured?: boolean;
          available_models?: Array<{
            id: string;
            name: string;
            description: string;
          }>;
        }>;
        projects: Array<{
          id: string;
          name: string;
          status: string;
          owner_name: string;
          llm_config: {
            model_provider: string;
            model_name: string;
          };
          chat_session_count: number;
          total_messages: number;
          total_tokens: number;
          agents_available: boolean;
          created_at: string;
          updated_at: string;
        }>;
        available_agents: Array<{
          type: string;
          name: string;
          name_ko?: string;
          description: string;
          description_ko?: string;
        }>;
        agent_settings?: Array<{
          type: string;
          name: string;
          name_ko?: string;
          description: string;
          description_ko?: string;
          model_tier?: string;
          actual_model?: string;
          provider?: string;
          cost_tier?: 'high' | 'medium' | 'low';
          recommended_for?: string;
          recommended_for_ko?: string;
          prompt_template?: string;
          tools_enabled?: string[];
          capabilities?: string[];
        }>;
        config_info?: {
          source: 'file' | 'env';
          path: string;
          editable_via_ui: boolean;
          restart_required: boolean;
        };
        model_tiers?: Record<string, {
          tier: string;
          provider: string;
          model: string;
          description: string;
          description_ko?: string;
          description_en?: string;
          cost_tier?: 'high' | 'medium' | 'low';
          default_parameters?: ModelParametersApi;
          parameters?: ModelParametersApi;
          parameter_constraints?: Record<string, ParameterConstraint>;
          parameter_restrictions?: Record<string, boolean>;
          default_builtin_tools?: Record<string, { enabled: boolean; parameters?: Record<string, unknown> }>;
          builtin_tools?: Record<string, { enabled: boolean; parameters?: Record<string, unknown> }>;
          available_builtin_tools?: Record<string, {
            name: string;
            description: string;
            description_ko?: string;
            enabled_by_default?: boolean;
            requires_api_key?: string | null;
            cost_info?: string | null;
            parameters?: Record<string, unknown>;
          }>;
        }>;
        available_tools?: Record<string, Array<{
          name: string;
          category: string;
          description: string;
          enabled: boolean;
          requires_api_key?: string;
        }>>;
        service_configs?: Record<string, {
          type: 'single_agent' | 'multi_agent';
          default_agent?: string;
          default_preset?: string;
          default_provider?: string;
          default_model?: string;
          available_agents?: string[];
          description: string;
        }>;
      };
    }

    const response = await api.get<BackendLlmConfigsResponse>('/admin/llm_configs');
    const data = response.data.data;

    // Transform providers
    const providers: Record<string, LlmProviderConfig> | undefined = data.providers
      ? Object.fromEntries(
          Object.entries(data.providers).map(([key, p]) => [
            key,
            {
              available: p.available,
              apiKeyConfigured: p.api_key_configured,
              availableModels: p.available_models,
            },
          ])
        )
      : undefined;

    const projects: ProjectLlmInfo[] = data.projects.map((p) => ({
      id: p.id,
      name: p.name,
      status: p.status as 'active' | 'on_hold' | 'completed' | 'deleted',
      ownerName: p.owner_name,
      llmConfig: {
        modelProvider: p.llm_config.model_provider,
        modelName: p.llm_config.model_name,
      },
      chatSessionCount: p.chat_session_count,
      totalMessages: p.total_messages,
      totalTokens: p.total_tokens,
      agentsAvailable: p.agents_available,
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    }));

    const availableAgents: AgentInfo[] = data.available_agents.map((a) => ({
      type: a.type,
      name: a.name,
      nameKo: a.name_ko,
      description: a.description,
      descriptionKo: a.description_ko,
    }));

    // Transform agent settings with model tiers
    const agentSettings: AgentInfo[] | undefined = data.agent_settings?.map((a) => ({
      type: a.type,
      name: a.name,
      nameKo: a.name_ko,
      description: a.description,
      descriptionKo: a.description_ko,
      modelTier: a.model_tier,
      actualModel: a.actual_model,
      provider: a.provider,
      costTier: a.cost_tier,
      recommendedFor: a.recommended_for,
      recommendedForKo: a.recommended_for_ko,
      promptTemplate: a.prompt_template,
      toolsEnabled: a.tools_enabled,
      capabilities: a.capabilities,
    }));

    // Transform config info
    const configInfo: AgentConfigInfo | undefined = data.config_info
      ? {
          source: data.config_info.source,
          path: data.config_info.path,
          editableViaUi: data.config_info.editable_via_ui,
          restartRequired: data.config_info.restart_required,
        }
      : undefined;

    // Helper function to transform built-in tools (snake_case → camelCase)
    const transformBuiltinToolConfig = (
      tools: Record<string, {
        name: string;
        description: string;
        description_ko?: string;
        enabled_by_default?: boolean;
        requires_api_key?: string | null;
        cost_info?: string | null;
        parameters?: Record<string, unknown>;
      }>
    ): Record<string, BuiltInToolConfig> => {
      return Object.fromEntries(
        Object.entries(tools).map(([toolName, config]) => [
          toolName,
          {
            name: config.name,
            description: config.description,
            descriptionKo: config.description_ko,
            enabledByDefault: config.enabled_by_default,
            requiresApiKey: config.requires_api_key,
            costInfo: config.cost_info,
            parameters: config.parameters,
          } as BuiltInToolConfig,
        ])
      );
    };

    // Transform model tiers
    const modelTiers: Record<string, ModelTierInfo> | undefined = data.model_tiers
      ? Object.fromEntries(
          Object.entries(data.model_tiers).map(([key, t]) => [
            key,
            {
              tier: t.tier,
              provider: t.provider,
              model: t.model,
              description: t.description,
              descriptionKo: t.description_ko,
              descriptionEn: t.description_en,
              costTier: t.cost_tier,
              defaultParameters: t.default_parameters,
              parameters: t.parameters,
              parameterConstraints: t.parameter_constraints,
              parameterRestrictions: t.parameter_restrictions,
              defaultBuiltinTools: t.default_builtin_tools,
              builtinTools: t.builtin_tools,
              availableBuiltinTools: t.available_builtin_tools
                ? transformBuiltinToolConfig(t.available_builtin_tools)
                : undefined,
            },
          ])
        )
      : undefined;

    // Transform available tools
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const availableTools: Record<string, ToolInfo[]> | undefined = data.available_tools
      ? Object.fromEntries(
          Object.entries(data.available_tools).map(([category, tools]) => [
            category,
            (tools as any[]).map((t) => ({
              name: t.name,
              category: t.category,
              description: t.description,
              enabled: t.enabled,
              requiresApiKey: t.requires_api_key,
              version: t.version,
              parameters: t.parameters,
              usableByAgents: t.usable_by_agents,
            })),
          ])
        )
      : undefined;

    // Transform service configs
    const serviceConfigs: Record<string, ServiceConfig> | undefined = data.service_configs
      ? Object.fromEntries(
          Object.entries(data.service_configs).map(([key, s]) => [
            key,
            {
              type: s.type,
              defaultAgent: s.default_agent,
              defaultPreset: s.default_preset,
              defaultProvider: s.default_provider,
              defaultModel: s.default_model,
              availableAgents: s.available_agents,
              description: s.description,
            },
          ])
        )
      : undefined;

    return {
      providers,
      projects,
      availableAgents,
      agentSettings,
      modelTiers,
      availableTools,
      serviceConfigs,
      configInfo,
    };
  },

  // PATCH /api/v1/admin/llm_configs/services/:service_name
  async updateServiceConfig(
    serviceName: string,
    data: {
      type?: 'single_agent' | 'multi_agent';
      default_agent?: string;
      default_preset?: string;
      default_provider?: string;
      default_model?: string;
      available_agents?: string[];
    }
  ): Promise<void> {
    await api.patch(`/admin/llm_configs/services/${serviceName}`, { service: data });
  },

  // PATCH /api/v1/admin/llm_configs/model_tiers/:tier_name
  async updateModelTier(
    tierName: string,
    data: {
      provider?: string;
      model?: string;
      parameters?: ModelParametersApi;
      builtin_tools?: Record<string, BuiltInToolSettings>;
    }
  ): Promise<void> {
    await api.patch(`/admin/llm_configs/model_tiers/${tierName}`, { model_tier: data });
  },

  // GET /api/v1/admin/parameter_constraints (from AI Engine)
  async getParameterConstraints(): Promise<Record<string, Record<string, ParameterConstraint>>> {
    interface BackendParameterConstraintsResponse {
      success: boolean;
      data: Record<string, Record<string, ParameterConstraint>>;
    }
    const response = await api.get<BackendParameterConstraintsResponse>('/admin/llm_configs/parameter_constraints');
    return response.data.data;
  },
};
